import DOMPurify from 'dompurify';
import { startCase, toLower } from 'lodash';

import {
  hasProductAdminAccess,
  hasProductCEAccess,
  hasProductCRMAccess,
  hasSiteAdminAccess,
  hasUserAdminAccess,
} from '@utils/AuthUtil';

const defaultOptions = {
  locale: Locale.EN_US,
  currency: Currency.USD,
};

export const MAX_CUMULITIVE_ATTACHMENT_SIZE = 25 * 1024 * 1024;

// Supported onboarding modal fields. `name` is always collected and cannot be removed.
export const ONBOARDING_MODAL_FIELDS = ['name', 'address', 'phone'];
const ALLOWED_ONBOARDING_MODAL_FIELDS = new Set(ONBOARDING_MODAL_FIELDS);

// Whitelist, dedup, and guarantee `name` is present. Falls back to ['name'] when empty/invalid.
export const normalizeOnboardingModalFields = (fields?: string[]): string[] => {
  const source = Array.isArray(fields) ? fields : [];
  const normalized = source.filter(
    (field, index) => ALLOWED_ONBOARDING_MODAL_FIELDS.has(field) && source.indexOf(field) === index
  );

  // `name` is always required and rendered first; keep the remaining whitelisted fields in order.
  const withoutName = normalized.filter((field) => field !== 'name');

  return ['name', ...withoutName];
};

export const dateFormat = 'MM/DD/YYYY';

export const getEmptyErrorState = (): IFieldValidation => ({
  errorMessage: '',
  errorState: null,
});

export const MAX_ATTACHMENT_FILE_SIZE = 25 * 1024 * 1024;

export const LIMIT = 20;

export const getCurrency = (value: string | number, options: IIntlOptions = defaultOptions): string => {
  if (!value) {
    return 'Invalid value';
  }

  const intlOptions = {
    style: 'currency',
    currency: options.currency,
  };

  return new Intl.NumberFormat(options.locale, intlOptions).format(+value);
};

export const getLargeCurrency = (value: string, options: IIntlOptions = defaultOptions): string => {
  if (!value) {
    return 'Invalid value';
  }

  const isNegative = value.startsWith('-');
  const cleanValue = value.replace(/[,$-]/g, '');

  if (!/^\d*\.?\d*$/.test(cleanValue)) {
    return 'Invalid value';
  }

  const formatLargeNumber = (numStr: string) => {
    const parts = numStr.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1] || '00';

    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return `${formattedInteger}.${decimalPart.padEnd(2, '0').slice(0, 2)}`;
  };

  const formattedNumber = formatLargeNumber(cleanValue);

  const finalNumber = isNegative ? `-${formattedNumber}` : formattedNumber;

  switch (options.currency) {
    case Currency.USD:
      return `$${finalNumber}`;
    default:
      return `${options.currency} ${finalNumber}`;
  }
};

export const appURLs = {
  // main urls
  setupPartialURL: 'setup',
  dashboardPartialURL: 'dashboard',
  reportsPartialURL: 'reports',
  casesPartialURL: 'case',
  customerSubmissionsPartialURL: 'customer-submissions',
  profilePartialURL: 'profile',
  // agency setup
  agencySetupPartialURL: 'setup/agency',
  agencyInfoPartialURL: 'agency-info',
  usersPartialURL: 'users-management',
  departmentsURL: 'departments',
  groupsPartialURL: 'groups',
  contactsPartialURL: 'contacts',
  commlinkPartialURL: 'commlink',
  entitiesPartialURL: 'entities',
  locationsPartialURL: 'location',
  locationsFieldMappingPartialURL: 'field-mapping',
  notificationSettingsPartialURL: 'notification-settings',
  slaSettingsPartialURL: 'sla-settings',
  auditTrailPartialURL: 'audit-trails',
  apiCredentials: 'api-credentials',
  paymentPortalPartialURL: 'payment-portal',
  collectionsPartialURL: 'collections-settings',
  // product settings - CE
  productSetupPartialURL: 'setup/products',
  finesPartialURL: 'fines',
  noticesPartialURL: 'notices',
  violationsPartialURL: 'violations',
  caseManagementPartialURL: 'case-management',
  rulesManagementPartialURL: 'rules-management',
  financePartialURL: 'finance',
  // product settings - CRM
  categoriesPartialURL: 'categories',
  faqManagementPartialURL: 'faq-management',
  templateSettingsPartialURL: 'template-settings',
  assignmentRulesSettingsPartialURL: 'assignment-rules',
  submissionSettingsPartialURL: 'submission-settings',
  mobileAppSettingsPartialURL: 'mobile-app-settings',
  // CCP, CDP, CSDP
  casePartialURL: 'products/cases',
  caseCreatePartialURL: 'products/cases/create',
  customerSubmissionPartialURL: 'products/customer-submission',
  createCustomerSubmissionPartialURL: 'products/customer-submission/create',
  actionCenterPartialURL: 'action-center',
  // Finance
  transactionListingPartialUrl: 'transaction/listing',
  paymentCollectionPartialUrl: 'payment/collection',
  financeReportPartialUrl: 'report/listing',
};

export const pageTitle = {
  dashboard: 'Dashboard',
  reports: 'Reports',
  setup: 'Agency Setup',
  cases: 'Case Listing Page',
  createCase: 'Create a Case Page',
  caseDetailPage: 'Case Detail Page',
  profile: 'User Profile',
  customerSubmissions: 'Customer Submissions Page',
  customerSubmissionDetailPage: 'Customer Submission Detail Page',
  createCustomerSubmission: 'Create Customer Submission Page',
  contactsPage: 'Contacts Page',
  commlinkPage: 'CommLink',
  financePage: 'Finance',
  entitiesPage: 'Entities',
  actionCenter: 'Action Center',
};

export const getLandingPagePartialURL = (userProfile: IUserProfile) => {
  const canAccessDashboard = hasProductCEAccess(userProfile) || hasProductCRMAccess(userProfile);
  const canAccessSetup =
    hasProductAdminAccess(ConfigProductType.XERCE, userProfile) ||
    hasProductAdminAccess(ConfigProductType.CRM, userProfile) ||
    hasSiteAdminAccess(userProfile) ||
    hasUserAdminAccess(userProfile);

  if (canAccessDashboard) {
    return appURLs.dashboardPartialURL;
  } else if (canAccessSetup) {
    return appURLs.agencySetupPartialURL;
  } else {
    return '/';
  }
};

export const getViolationTypeColor = (url: string = 'animal') => {
  // Left borders are hard-coded as per business rules.
  const colors = {
    animal: '#d54900',
    vehicle: '#99cf55',
    others: '#c567fa',
    general: '#4934a0',
    light: '#95f800',
    graffiti: '#d54900',
    custom: '#a700ba',
    environmental: '#2ECC71',
    publicNuisance: '#9B59B6',
    publicWorks: '#E67E22',
    buildingAndFireSafety: '#E74C3C',
  };
  const splitString = url.split('/');

  return colors[splitString[splitString.length - 1].replace('.png', '')] || colors.animal;
};

export const pluralize = (num: number, singularTerm: string, pluralTerm: string, isNumberIncluded: boolean = true) => {
  if (isNaN(num)) {
    return 'NaN';
  }

  return `${isNumberIncluded ? `${num} ` : ''}${num === 1 ? singularTerm : pluralTerm}`;
};

export const isTablet = (): boolean => navigator.userAgent.match(/iPad/i) != null;

export const isMobile = (): boolean =>
  navigator.userAgent.match(/Android/i) != null || navigator.userAgent.match(/iPhone/i) != null;

export const isIphone = (): boolean => navigator.userAgent.match(/iPhone/i) != null;

export const isSafari = (): boolean => navigator.userAgent.match(/CriOS/i) == null;

export const isDesktop = (): boolean => window.screen.width > 1024;

export const isIE = (): boolean => false || !!(document as any).documentMode;

export const isEdge = (): boolean => !isIE() && !!(window as any).StyleMedia;

export const scrollTo = (querySelector: string) => (e?: React.SyntheticEvent<MouseEvent>) => {
  const elem = document.querySelector(querySelector);
  if (elem) {
    elem.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    requestAnimationFrame(() => {
      const modalBody = document.querySelector('.modal-body');
      if (modalBody) {
        modalBody.scrollLeft = 0;
      }
    });
  }
};

export const getFormattedCurrencyAmount = (value: string) => {
  const amount = value.match(/^\d+\.\d{0,3}$/);

  let formatedValue = amount ? amount[0] : null;
  if (formatedValue) {
    formatedValue =
      formatedValue.split('.')[1] !== undefined
        ? formatedValue.split('.')[1].length !== 1
          ? (parseFloat(formatedValue) * 10).toFixed(2)
          : (parseFloat(formatedValue) / 10).toFixed(2)
        : (parseFloat(formatedValue) / 100).toFixed(2);
  }

  return formatedValue;
};

export const getDecimalCount = (value: number | string) => {
  const decimals = value.toString().split('.')[1];
  if (decimals) {
    return decimals.length;
  }

  return 0;
};

export const getBestMatchSortedList = (searchQuery) => {
  const getViolationListLabel = (elem) => `${elem.label} ${elem.configMunicipalCode.articleNumber}`;
  const getIndexOf = (elem) => getViolationListLabel(elem).toLowerCase().indexOf(searchQuery.toLowerCase());

  const sortByBestMatch = (curr, next) =>
    (getIndexOf(curr) === 0 && getIndexOf(next) === 0) || (getIndexOf(curr) !== 0 && getIndexOf(next) !== 0)
      ? getViolationListLabel(curr).toLowerCase() < getViolationListLabel(next).toLowerCase()
        ? -1
        : 1
      : getViolationListLabel(next).toLowerCase().indexOf(searchQuery.toLowerCase()) === 0
      ? 1
      : -1;

  return sortByBestMatch;
};

export const randomCodeGenerator = (length: number) =>
  Math.random()
    .toString(36)
    .substr(2, length + 2);

export const humanFileSize = (bytes) => {
  const threshold = 1024;
  let size = bytes;

  if (Math.abs(bytes) < threshold) {
    return `${bytes} B`;
  }

  // We don't support sizes greater than 15 MB.
  const units = ['kB', 'MB'];
  let currentUnitIndex = -1;

  do {
    size /= threshold;
    ++currentUnitIndex;
  } while (Math.abs(size) >= threshold && currentUnitIndex < units.length - 1);

  return `${size.toFixed(1)} ${units[currentUnitIndex]}`;
};

export const getCaseInitiationType = (type: CaseInitiationTypeOptions) => {
  switch (type) {
    case CaseInitiationTypeOptions.PROACTIVE:
      return 'Proactive';
    case CaseInitiationTypeOptions.REACTIVE:
      return 'Reactive';
    default:
      return 'Unknown';
  }
};

export const getS3URL = (response: any): string => {
  const location = response?.documentElement?.getElementsByTagName('Location')?.[0]?.childNodes?.[0]?.data;

  if (!location) {
    return '';
  }

  // Keep S3's Location encoded. Decoding the full URL turns %23/%3F/%2F into
  // fragment, query, and path delimiters before the server parses the key.
  return location;
};

export const isImage = (fileName: string) => {
  if (!fileName) {
    return false;
  }

  const fileNameSplit: string[] = fileName.split('.');
  const fileNameExtension: string = fileNameSplit[fileNameSplit.length - 1];

  return /(jpe?g|png)$/.test(fileNameExtension.toLowerCase());
};

export const getIndexedFileName = (fileNameWithExtension: string) => {
  const fileNameSplit: string[] = fileNameWithExtension.split('.');
  fileNameSplit[fileNameSplit.length - 2] = `${fileNameSplit[fileNameSplit.length - 2]}_${Date.now().toString()}`;

  return fileNameSplit.join('.');
};

export const getFileName = (fileURL: string) => {
  const objects = fileURL.split('/');

  return objects[objects.length - 1];
};

export const getFileType = (file: ICSAttachmentResponse | ICRMIssueAttachment) => {
  const fileNameSplit: string[] = file.fileName.split('.');
  const fileNameExtension: string = fileNameSplit[fileNameSplit.length - 1];

  return /(jpe?g|png)$/.test(fileNameExtension.toLowerCase()) ? 'image' : 'others';
};

export const getPaginationParams = (
  params: ILoadMoreParams,
  localOffset: number,
  totalCount: number
): IUserFetchParams => {
  const { query: q, limit, offset } = params;

  if (offset === 0 || (offset > 0 && totalCount > offset)) {
    const listParams: IUserFetchParams = {
      limit,
      offset: offset === 0 ? 0 : localOffset,
      q,
    };

    return listParams;
  }

  return null;
};

export const getUsersOptions = (
  users: ICSUser[],
  selectedUsers?: ICSUser[],
  showDeactivatedUsers = false
): IPickListOptions[] => {
  const selectedUsersCopy: ICSUser[] = selectedUsers && JSON.parse(JSON.stringify(selectedUsers));

  const options = users.map((user) => {
    const currentUserSelected = selectedUsers && selectedUsers.findIndex((selectedUser) => selectedUser.id === user.id);
    if (currentUserSelected > -1) {
      selectedUsersCopy.splice(currentUserSelected, 1);
    }

    return {
      value: user.id,
      label: `${user.firstName} ${user.lastName}`,
    };
  });

  if (selectedUsers && selectedUsersCopy.length) {
    selectedUsersCopy.forEach((user) => {
      const selectedOption = {
        value: user.id,
        label: addDeactivatedString(`${user.firstName} ${user.lastName}`, showDeactivatedUsers && user.isActive),
        ...(showDeactivatedUsers ? { isDisabled: user?.isActive === false } : {}),
      };

      options.push(selectedOption);
    });
  }

  return options;
};

export const getOrdinalSuffixOf = (i) => {
  const j = i % 10;
  const k = i % 100;
  if (j === 1 && k !== 11) {
    return `${i}st`;
  }
  if (j === 2 && k !== 12) {
    return `${i}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${i}rd`;
  }

  return `${i}th`;
};

export const getFloatAmount = (amount: number | string) => (amount ? parseFloat(amount.toString()).toFixed(2) : '0.00');

export const getInstanceWatermark = () => {
  const adminHostUrl = process.env.ADMIN_HOST_URL?.split('/');
  const urlBasePath = adminHostUrl[adminHostUrl.length - 1];

  const urlPrimaryName = urlBasePath.split('.')[0];

  if (!isNaN(urlPrimaryName as any)) {
    return `CI - ${urlPrimaryName}`;
  } else if (urlPrimaryName.includes('qa')) {
    return 'QA';
  } else if (urlPrimaryName.includes('stage')) {
    return 'Stage';
  }

  return null;
};

export const isProductionEnv = () => process.env.ENVIRONMENT === 'production';

export const sentenceCaseStr = (str: string) => startCase(toLower(str));

export const sanitizeInput = (input): string =>
  typeof input === 'boolean' ? input.toString() : DOMPurify.sanitize(input) || '';

/**
 * currently this method is being used to append 'deactivated' to username
 * @param word {string}
 * @param isActive {boolean}
 * @returns {string}
 */
export const addDeactivatedString = (word: string, isActive: boolean) =>
  `${word.trim()}${isActive === false ? ' (Deactivated)' : ''}`;
